import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { ConfigService } from './config.service';

export interface DynamicWidget {
  id: string;
  name: string;
  type: 'iframe' | 'markdown' | 'component';
  title: string;
  path: string[];
  icon: string | null;
  url: string | null;
  content: string | null;
  source: 'team' | 'user' | 'system';
  config: Record<string, any>;
  /** 'launcher' (default) or 'panel' = always in the app rail, opened as a first-class pane */
  surface?: 'launcher' | 'panel';
  /** Placement kind for the window manager (default 'app') */
  kind?: 'session' | 'document' | 'terminal' | 'tool' | 'app';
  // Component-specific fields (for web component apps)
  element?: string;
  scripts?: string[];
  styles?: string[];
  basePath?: string;
}

interface WidgetsResponse {
  success: boolean;
  data: DynamicWidget[];
  count: number;
}

@Injectable({
  providedIn: 'root'
})
export class DynamicWidgetsService {
  private _widgets$ = new BehaviorSubject<DynamicWidget[]>([]);
  private _loading$ = new BehaviorSubject<boolean>(false);
  private _loadedTeam: string | null = null;

  public readonly widgets$ = this._widgets$.asObservable();
  public readonly loading$ = this._loading$.asObservable();

  constructor(private http: HttpClient) {}

  /** App badges (host.badge.set): app manifest name → count. Per browser tab,
   *  never persisted — the app re-sets it on load. Rendered on launcher rows,
   *  rail pins and the open panel's tab. */
  private readonly _badges = new Map<string, number>();
  public readonly badges$ = new BehaviorSubject<ReadonlyMap<string, number>>(this._badges);

  setBadge(appName: string, count: number | null | undefined): void {
    const n = Math.max(0, Math.floor(Number(count) || 0));
    if (n > 0) this._badges.set(appName, n); else this._badges.delete(appName);
    this.badges$.next(new Map(this._badges));
  }

  badgeFor(appName: string | undefined): number {
    return appName ? (this._badges.get(appName) || 0) : 0;
  }

  static badgeLabel(n: number): string {
    return n > 99 ? '99+' : String(n);
  }

  /**
   * Get all dynamic widgets for a team, loading from API if not already loaded
   */
  async getWidgets(team?: string): Promise<DynamicWidget[]> {
    if (this._loadedTeam !== team) {
      await this.loadWidgets(team);
    }
    return this._widgets$.getValue();
  }

  /**
   * Load widgets from the mux API
   */
  async loadWidgets(team?: string): Promise<void> {
    this._loading$.next(true);
    try {
      const params = team ? `?team=${encodeURIComponent(team)}` : '';
      const response = await firstValueFrom(
        this.http.get<WidgetsResponse>(`${ConfigService.muxUrl}/widgets${params}`)
      );
      if (response.success) {
        this._widgets$.next(response.data);
        this._loadedTeam = team ?? null;
      }
    } catch (error) {
      console.error('Failed to load dynamic widgets:', error);
      this._widgets$.next([]);
    } finally {
      this._loading$.next(false);
    }
  }

  /**
   * Reload widgets from the API (invalidate cache)
   */
  async reloadWidgets(team?: string): Promise<void> {
    try {
      const params = team ? `?team=${encodeURIComponent(team)}` : '';
      await firstValueFrom(
        this.http.post<any>(`${ConfigService.muxUrl}/widgets/reload${params}`, {})
      );
      await this.loadWidgets(team);
    } catch (error) {
      console.error('Failed to reload widgets:', error);
    }
  }

  /**
   * Get widget by ID
   */
  getWidget(id: string): DynamicWidget | undefined {
    return this._widgets$.getValue().find(w => w.id === id);
  }

  /**
   * Get widgets grouped by their path (for menu hierarchy)
   */
  getWidgetsGrouped(): Map<string, DynamicWidget[]> {
    const widgets = this._widgets$.getValue();
    const groups = new Map<string, DynamicWidget[]>();

    for (const widget of widgets) {
      // Use first path segment as group, or 'Custom' if at root
      const groupName = widget.path.length > 1 ? widget.path[0] : 'Custom';
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(widget);
    }

    return groups;
  }
}
